/**
 * Persist the patient's bed/room entry URL so they can reopen without scanning QR.
 * Keys survive selective session clears on patient pages.
 */

import {
    isPatientFacingPath as policyIsPatientFacingPath,
    isStablePatientEntryPath as policyIsStablePatientEntryPath,
    resolveSavedRoomRedirect,
    shouldBlockCallForA2hs as policyShouldBlockCallForA2hs,
    isMobileUserAgent as policyIsMobileUserAgent,
} from './patientAccessPolicy';

export const PATIENT_ACCESS_KEYS = [
    'liveassist_room_entry_path',
    'liveassist_room_entry_at',
    'liveassist_a2hs_dismissed_until',
    'liveassist_pwa_hint_seen',
    'liveassist_a2hs_gate_done',
    'liveassist_a2hs_installed',
];

const ROOM_PATH_KEY = 'liveassist_room_entry_path';
const ROOM_AT_KEY = 'liveassist_room_entry_at';
const ROOM_COOKIE = 'liveassist_room_entry';
const ROOM_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 400; // ~13 months
/** @deprecated permanent flag — no longer used to skip the Home Screen prompt */
const A2HS_GATE_KEY = 'liveassist_a2hs_gate_done';
/** This visit only: patient answered Continue / Done so auto-call can start */
const A2HS_GATE_SESSION_KEY = 'liveassist_a2hs_gate_session';
/** Gate UI is open — call must not start until answered */
const A2HS_BLOCKING_KEY = 'liveassist_a2hs_blocking';
export const A2HS_GATE_EVENT = 'liveassist-a2hs-gate-done';
export const A2HS_REQUIRED_EVENT = 'liveassist-a2hs-required';
export const APP_DISPLAY_NAME = 'Live Assist Call';
const A2HS_VENDOR_FLAG_KEY = 'liveassist_a2hs_vendor_enabled';
const A2HS_INSTALLED_KEY = 'liveassist_a2hs_installed';

export function isPatientFacingPath(pathname = '') {
    return policyIsPatientFacingPath(pathname);
}

export function isStablePatientEntryPath(pathname = '') {
    return policyIsStablePatientEntryPath(pathname);
}

/** Persist vendor Add-to-Home Yes/No for the patient gate (from room API). */
export function setVendorAddToHomeScreenEnabled(enabled) {
    try {
        sessionStorage.setItem(A2HS_VENDOR_FLAG_KEY, enabled ? '1' : '0');
        window.dispatchEvent(new CustomEvent('liveassist-a2hs-vendor-flag', {
            detail: { enabled: !!enabled },
        }));
    } catch (_e) {
        /* ignore */
    }
}

/**
 * Vendor config: show Add to Home Screen gate?
 * Default true until room API loads (keeps current hospital behavior).
 */
export function isVendorAddToHomeScreenEnabled() {
    try {
        const raw = sessionStorage.getItem(A2HS_VENDOR_FLAG_KEY);
        if (raw == null) {
            return true;
        }
        return raw === '1';
    } catch (_e) {
        return true;
    }
}

function writeRoomCookie(path) {
    try {
        if (typeof document === 'undefined') {
            return;
        }
        const secure = typeof window !== 'undefined' && window.location?.protocol === 'https:'
            ? ';Secure'
            : '';
        document.cookie = `${ROOM_COOKIE}=${encodeURIComponent(path)}`
            + `;path=/;max-age=${ROOM_COOKIE_MAX_AGE_SEC};SameSite=Lax${secure}`;
    } catch (_e) {
        /* ignore */
    }
}

function readRoomCookie() {
    try {
        if (typeof document === 'undefined' || !document.cookie) {
            return '';
        }
        const parts = document.cookie.split(';');
        for (let i = 0; i < parts.length; i += 1) {
            const part = parts[i].trim();
            if (part.indexOf(`${ROOM_COOKIE}=`) === 0) {
                return decodeURIComponent(part.slice(ROOM_COOKIE.length + 1));
            }
        }
        return '';
    } catch (_e) {
        return '';
    }
}

export function savePatientRoomEntry(pathname, search = '') {
    try {
        const pathOnly = String(pathname || '').split('?')[0];
        if (!pathOnly || !isStablePatientEntryPath(pathOnly)) {
            return;
        }
        const path = `${pathOnly}${search || ''}`;
        localStorage.setItem(ROOM_PATH_KEY, path);
        localStorage.setItem(ROOM_AT_KEY, String(Date.now()));
        writeRoomCookie(path);
    } catch (_e) {
        try {
            const pathOnly = String(pathname || '').split('?')[0];
            if (pathOnly && isStablePatientEntryPath(pathOnly)) {
                writeRoomCookie(`${pathOnly}${search || ''}`);
            }
        } catch (_err) {
            /* ignore */
        }
    }
}

export function getPatientRoomEntry() {
    try {
        let saved = localStorage.getItem(ROOM_PATH_KEY) || '';
        if (!saved) {
            saved = readRoomCookie();
            // Restore localStorage from cookie after reopen / storage clear
            if (saved && isStablePatientEntryPath(saved.split('?')[0])) {
                try {
                    localStorage.setItem(ROOM_PATH_KEY, saved);
                    localStorage.setItem(ROOM_AT_KEY, String(Date.now()));
                } catch (_e) {
                    /* ignore */
                }
            }
        }
        if (saved && isStablePatientEntryPath(String(saved).split('?')[0])) {
            return saved;
        }
        return '';
    } catch (_e) {
        const fromCookie = readRoomCookie();
        return fromCookie && isStablePatientEntryPath(fromCookie.split('?')[0]) ? fromCookie : '';
    }
}

export function clearPatientRoomEntry() {
    try {
        localStorage.removeItem(ROOM_PATH_KEY);
        localStorage.removeItem(ROOM_AT_KEY);
    } catch (_e) {
        /* ignore */
    }
    try {
        document.cookie = `${ROOM_COOKIE}=;path=/;max-age=0;SameSite=Lax`;
    } catch (_e) {
        /* ignore */
    }
}

/**
 * Open last scanned bed URL when patient returns via Home Screen /patient-home.
 * Returns true if a redirect was started.
 */
export function tryRedirectToSavedPatientRoom() {
    if (typeof window === 'undefined') {
        return false;
    }
    const target = resolveSavedRoomRedirect({
        currentPath: window.location.pathname || '',
        savedEntry: getPatientRoomEntry(),
    });
    if (!target) {
        return false;
    }
    try {
        window.location.replace(target);
        return true;
    } catch (_e) {
        return false;
    }
}

/**
 * Patient pages historically called localStorage.clear() — that wiped the saved room
 * and Add-to-Home dismiss state. Preserve access keys (+ language).
 */
export function clearPatientSessionKeepAccess() {
    const keep = {};
    try {
        PATIENT_ACCESS_KEYS.forEach((key) => {
            const value = localStorage.getItem(key);
            if (value != null) {
                keep[key] = value;
            }
        });
        const language = localStorage.getItem('language');
        if (language != null) {
            keep.language = language;
        }
        localStorage.clear();
        Object.keys(keep).forEach((key) => {
            localStorage.setItem(key, keep[key]);
        });
    } catch (_e) {
        try {
            localStorage.clear();
        } catch (_err) {
            /* ignore */
        }
    }
}

export function isA2hsDismissed() {
    try {
        const until = Number(localStorage.getItem('liveassist_a2hs_dismissed_until') || 0);
        return Number.isFinite(until) && until > Date.now();
    } catch (_e) {
        return false;
    }
}

export function dismissA2hs(days = 14) {
    try {
        const ms = Math.max(1, days) * 24 * 60 * 60 * 1000;
        localStorage.setItem('liveassist_a2hs_dismissed_until', String(Date.now() + ms));
    } catch (_e) {
        /* ignore */
    }
}

/**
 * True only after patient answers the Home Screen prompt for THIS scan/visit,
 * or when already running as the installed Home Screen app,
 * or when vendor disabled Add to Home Screen (No),
 * or when not on a mobile device (desktop never shows the popup).
 */
export function hasCompletedA2hsGate() {
    try {
        if (!isMobilePatientDevice()) {
            return true;
        }
        if (isPatientHomeScreenAlreadyInstalled()) {
            return true;
        }
        if (!isVendorAddToHomeScreenEnabled()) {
            return true;
        }
        // Popup is open — never treat as completed
        if (sessionStorage.getItem(A2HS_BLOCKING_KEY) === '1') {
            return false;
        }
        return sessionStorage.getItem(A2HS_GATE_SESSION_KEY) === '1';
    } catch (_e) {
        return false;
    }
}

/** True when Add-to-Home must be shown and answered before the call. */
export function shouldBlockCallForA2hs() {
    try {
        return policyShouldBlockCallForA2hs({
            vendorEnabled: isVendorAddToHomeScreenEnabled(),
            isPwa: isPatientHomeScreenAlreadyInstalled(),
            isMobile: isMobilePatientDevice(),
            sessionAnswered: sessionStorage.getItem(A2HS_GATE_SESSION_KEY) === '1',
            gateBlocking: sessionStorage.getItem(A2HS_BLOCKING_KEY) === '1',
        });
    } catch (_e) {
        return false;
    }
}

/** Open the blocking gate UI (call must wait). */
export function beginA2hsGate() {
    try {
        sessionStorage.removeItem(A2HS_GATE_SESSION_KEY);
        sessionStorage.setItem(A2HS_BLOCKING_KEY, '1');
        localStorage.removeItem(A2HS_GATE_KEY);
    } catch (_e) {
        /* ignore */
    }
}

/** Start of a new QR / room landing — re-ask only on mobile when not installed. */
export function resetA2hsGateForNewScan() {
    if (!isMobilePatientDevice() || isPatientHomeScreenAlreadyInstalled()) {
        markA2hsGateDone();
        return;
    }
    beginA2hsGate();
}

export function markA2hsGateDone() {
    try {
        sessionStorage.setItem(A2HS_GATE_SESSION_KEY, '1');
        sessionStorage.removeItem(A2HS_BLOCKING_KEY);
        window.dispatchEvent(new CustomEvent(A2HS_GATE_EVENT, { detail: { answered: true } }));
    } catch (_e) {
        try {
            window.dispatchEvent(new CustomEvent(A2HS_GATE_EVENT, { detail: { answered: true } }));
        } catch (_err) {
            /* ignore */
        }
    }
}

/** Tell the gate UI it must open (room API decided A2HS is required). */
export function requestA2hsGateUi() {
    // Desktop / already installed → never open or block the call
    if (!shouldShowA2hsGate()) {
        markA2hsGateDone();
        return;
    }
    beginA2hsGate();
    try {
        window.dispatchEvent(new CustomEvent(A2HS_REQUIRED_EVENT));
    } catch (_e) {
        /* ignore */
    }
}

/** Persist that the patient installed the Home Screen shortcut. */
export function markPatientHomeScreenInstalled() {
    try {
        localStorage.setItem(A2HS_INSTALLED_KEY, '1');
    } catch (_e) {
        /* ignore */
    }
}

export function hasMarkedPatientHomeScreenInstalled() {
    try {
        return localStorage.getItem(A2HS_INSTALLED_KEY) === '1';
    } catch (_e) {
        return false;
    }
}

/**
 * Phones / tablets only — never show Save on Home Screen on desktop/laptop.
 */
export function isMobilePatientDevice() {
    try {
        return policyIsMobileUserAgent(navigator.userAgent || '', {
            maxTouchPoints: navigator.maxTouchPoints || 0,
            platform: navigator.platform || '',
        });
    } catch (_e) {
        return false;
    }
}

/**
 * Show the Save on Home Screen popup only when useful:
 * mobile + vendor Yes + not already installed / running as PWA.
 */
export function shouldShowA2hsGate() {
    try {
        if (!isMobilePatientDevice()) {
            return false;
        }
        if (!isVendorAddToHomeScreenEnabled()) {
            return false;
        }
        if (isRunningAsInstalledPwa()) {
            return false;
        }
        // Patient already added Live Assist to Home Screen earlier
        if (hasMarkedPatientHomeScreenInstalled()) {
            return false;
        }
        return true;
    } catch (_e) {
        return false;
    }
}

export function isRunningAsInstalledPwa() {
    try {
        if (window.matchMedia) {
            if (window.matchMedia('(display-mode: standalone)').matches) {
                return true;
            }
            if (window.matchMedia('(display-mode: fullscreen)').matches) {
                return true;
            }
            if (window.matchMedia('(display-mode: minimal-ui)').matches) {
                return true;
            }
        }
        // iOS Safari "Add to Home Screen"
        if (typeof navigator !== 'undefined' && navigator.standalone === true) {
            return true;
        }
        // Android TWA
        if (document.referrer && document.referrer.indexOf('android-app://') === 0) {
            return true;
        }
    } catch (_e) {
        /* ignore */
    }
    return false;
}

/** Installed Home Screen app OR previously completed install on this device. */
export function isPatientHomeScreenAlreadyInstalled() {
    return isRunningAsInstalledPwa() || hasMarkedPatientHomeScreenInstalled();
}

export function isIosSafari() {
    try {
        const ua = navigator.userAgent || '';
        const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        const webkit = /WebKit/.test(ua);
        const notOther = !/CriOS|FxiOS|EdgiOS|OPiOS|Chrome/.test(ua);
        return iOS && webkit && notOther;
    } catch (_e) {
        return false;
    }
}

/**
 * Real Chrome on Android (not Samsung Internet / WebView / other browsers).
 * Samsung Internet WebAPKs are often blocked by Play Protect as "Unsafe app".
 */
export function isAndroidChromeBrowser() {
    try {
        const ua = navigator.userAgent || '';
        if (!/Android/i.test(ua)) {
            return false;
        }
        if (/SamsungBrowser|EdgA|OPR\/|UCBrowser|MiuiBrowser|HeyTapBrowser|VivoBrowser|HuaweiBrowser|FQBrowser|YaBrowser/i.test(ua)) {
            return false;
        }
        if (/; wv\)/i.test(ua)) {
            return false;
        }
        return /Chrome\/\d+/i.test(ua);
    } catch (_e) {
        return false;
    }
}

/**
 * Patient should open Chrome before Add to Home Screen.
 * Lens Custom Tab + Samsung Internet / other browsers trigger Play Protect "Unsafe app blocked".
 */
export function needsOpenInChromeForHomeScreen() {
    try {
        if (isRunningAsInstalledPwa() || isIosSafari()) {
            return false;
        }
        if (sessionStorage.getItem('liveassist_chrome_persistent') === '1') {
            return false;
        }
        if (isOpenedFromGoogleLensOrEphemeral()) {
            return true;
        }
        const ua = navigator.userAgent || '';
        if (!/Android/i.test(ua)) {
            return false;
        }
        return !isAndroidChromeBrowser();
    } catch (_e) {
        return false;
    }
}

/**
 * Google Lens / Search often open a temporary tab (Custom Tab).
 * Browser Back returns to Lens and that temporary tab disappears from Chrome.
 */
export function isOpenedFromGoogleLensOrEphemeral() {
    try {
        if (isRunningAsInstalledPwa()) {
            return false;
        }
        if (sessionStorage.getItem('liveassist_chrome_persistent') === '1') {
            return false;
        }
        if (sessionStorage.getItem('liveassist_from_lens') === '1') {
            return true;
        }
        const ref = String(document.referrer || '');
        const fromLens =
            /android-app:\/\/com\.google\.android\.googlequicksearchbox/i.test(ref)
            || /android-app:\/\/com\.google\.ar\.lens/i.test(ref)
            || /lens\.google/i.test(ref)
            || /google\.[a-z.]+\/.+lens/i.test(ref);
        if (fromLens) {
            sessionStorage.setItem('liveassist_from_lens', '1');
            return true;
        }
        return false;
    } catch (_e) {
        return false;
    }
}

export function markPatientChromePersistent() {
    try {
        sessionStorage.setItem('liveassist_chrome_persistent', '1');
        sessionStorage.removeItem('liveassist_from_lens');
    } catch (_e) {
        /* ignore */
    }
}

/**
 * Open current room URL in real Chrome (persistent tab), not Lens Custom Tab.
 */
export function openPatientUrlInChrome(href) {
    try {
        markPatientChromePersistent();
        const absolute = href || `${window.location.origin}${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;
        const u = new URL(absolute, window.location.origin);
        const ua = navigator.userAgent || '';

        if (/Android/i.test(ua)) {
            // Prefer Chrome so Home Screen install is not blocked as "Unsafe app"
            // (common with Samsung Internet WebAPK / Play Protect).
            const pathAndQuery = `${u.pathname}${u.search}${u.hash}`;
            const intentUrl =
                `intent://${u.host}${pathAndQuery}`
                + `#Intent;scheme=${u.protocol.replace(':', '')}`
                + ';package=com.android.chrome'
                + ';action=android.intent.action.VIEW'
                + `;S.browser_fallback_url=${encodeURIComponent(u.href)}`
                + ';end';
            window.location.href = intentUrl;
            return true;
        }

        // iOS / desktop — open a normal browser tab when possible
        const opened = window.open(u.href, '_blank', 'noopener,noreferrer');
        if (!opened) {
            window.location.href = u.href;
        }
        return true;
    } catch (_e) {
        try {
            window.location.href = href || window.location.href;
            return true;
        } catch (_err) {
            return false;
        }
    }
}
